import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';
import plusIcon from 'assets/images/icons/plus.svg';
import scheduleIcon from 'assets/images/icons/schedule.svg';
import FormButton from 'components/form-components/FormButton';
import ScheduleEditModal from './scheduleEditModal/ScheduleEditModal';
import ScheduleListHeader from './ScheduleListHeader';
import ScheduleTasks from './schedule-tasks/ScheduleTasks';
import TableComponent from 'components/table-component-new/Table';
import TablePagination from 'components/table-component-new/TablePagination';
import { schedulesHeaderList } from '../utils';
import { CallAPI } from 'actions/General';

const ScheduleList = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [scheduleList, setScheduleList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [companyFieldOptions, setCompanyFieldOptions] = useState([]);
  const [taskLabelsOptions, setTaskLabelsOptions] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [tasksScheduleId, setTasksScheduleId] = useState(null);
  const [scheduleDetail, setScheduleDetail] = useState(null);
  const [filter, setFilter] = useState({});
  const [companyFieldIdRowIdToDisplayValueMap, setCompanyFieldIdRowIdToDisplayValueMap] =
    useState(null);

  const handleDebouncedFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const clearListing = () => {
    setSelectedRows([]);
    setScheduleList([]);
    setTotalRecords(0);
  };

  const fetchScheduleList = async (page = 1, limit = displayEntries) => {
    clearListing();
    const payload = { state: 'active', page, limit };
    if (filter.search) payload.searchText = filter.search;
    if (filter.status?.length) payload.status = filter.status;
    if (filter.priority?.length) payload.priority = filter.priority;
    if (filter.sortBy) payload.sortBy = filter.sortBy;
    if (filter.labels?.length) payload.labels = filter.labels;
    if (filter.companyFieldValues?.length && companyFieldIdRowIdToDisplayValueMap) {
      payload.companyFieldValues = filter.companyFieldValues.map(
        (v) => companyFieldIdRowIdToDisplayValueMap[v],
      );
    }

    // DUE DATE
    if (filter.dueDate?.type && filter.dueDate?.type !== 'all') {
      payload.dueDate = filter.dueDate.type;
      if (filter.dueDate?.type === 'custom') {
        const range = filter.dueDate.range;
        payload.dueFromDate = range[0];
        payload.dueToDate = range[1];
      }
    }

    const result = await CallAPI('GET_ALL_SCHEDULES', payload, null, setLoading);
    if (result.status) {
      result.data.list.forEach((s) => {
        s.displayRefNumber = `ST-${s.referenceNumber}`;
        s.formTitle = s.form?.title ?? '';
        s.companyFieldName = s.companyField?.fieldTitle ?? '';
      });
      setScheduleList(result.data.list);
      setTotalRecords(result.data.count);
    }
  };

  const fetchCompanyFieldList = async () => {
    const { status, data } = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
    if (!status || !data?.length) return;

    const fieldMap = {};
    // unique objects in payload
    const seen = {};
    const payload = { items: [] };

    for (const item of data) {
      fieldMap[item._id] = {
        label: item.fieldTitle,
        dataSourceId: item.dataSource._id,
        dataSourceColumn: item.dataSourceColumn,
      };

      const key = `${item.dataSource._id}_${item.dataSourceColumn}`;
      if (seen[key]) continue;
      seen[key] = true;

      payload.items.push({
        dataSourceId: item.dataSource._id,
        columnId: item.dataSourceColumn,
      });
    }

    const entriesResponse = await CallAPI('GET_MULTIPLE_DATASOURCE_ENTRIES', payload);
    if (entriesResponse.status) {
      const options = [];
      const companyFieldMap = {};
      for (const fieldId in fieldMap) {
        const { dataSourceId, label, dataSourceColumn } = fieldMap[fieldId];
        const ds = entriesResponse.data.find((d) => d.dataSourceId === dataSourceId);
        const entries = (ds?.entries || [])
          .filter((e) => e.entryValue?.keyValue && e.entryValue.columnId === dataSourceColumn)
          .map((e) => {
            const key = `${fieldId}_${e.rowId}`;
            const displayValue = e.entryValue.keyValue;
            companyFieldMap[key] = displayValue;
            return { value: key, displayValue: displayValue };
          });

        options.push({
          value: fieldId,
          displayValue: label,
          selectMultiple: true,
          children: entries,
        });
      }
      setCompanyFieldOptions(options);
      setCompanyFieldIdRowIdToDisplayValueMap(companyFieldMap);
    }
  };

  const actionsCallBack = async (action, data) => {
    if (action === 'edit' || action === 'duplicate') {
      const result = await CallAPI('GET_SCHEDULE_DETAIL', null, data.id);
      if (result.status) {
        setScheduleDetail(result.data);
        setEditMode(action);
      }
    } else if (action === 'close') {
      const result = await CallAPI('CLOSE_SCHEDULE', {}, data.id);
      if (result.status) {
        fetchScheduleList();
      } else {
        toast.error(result.error);
      }
    } else if (action === 'delete') {
      const payload = { scheduleIds: data.ids };
      const result = await CallAPI('DELETE_BULK_SCHEDULES', payload);
      if (result.status) {
        fetchScheduleList();
      } else {
        toast.error(result.error);
      }
    } else if (action === 'archive') {
      const payload = { scheduleIds: data.ids, state: 'archived' };
      const result = await CallAPI('UPDATE_SCHEDULES_STATE', payload);
      if (result.status) {
        fetchScheduleList();
      } else {
        toast.error(result.error);
      }
    }
  };

  const fetchTaskLabelList = async () => {
    const result = await CallAPI('GET_ALL_TASK_LABELS');
    if (result.status) {
      setTaskLabelsOptions(
        (result?.data ?? []).map(({ _id, name }) => ({
          value: _id,
          displayValue: name,
        })),
      );
    }
  };

  const onRowsSelection = (name, value) => {
    let tempSelection = [];
    const mappedObject = (obj) => ({
      id: obj._id,
      status: obj.status,
      scheduleType: obj.scheduleType,
      startDate: obj.startDate,
    });

    if (name === 'all') {
      if (value) tempSelection = scheduleList.map((s) => mappedObject(s));
      else tempSelection = [];
    } else {
      tempSelection = cloneDeep(selectedRows);

      if (value) {
        const selectedSchedule = scheduleList.find((t) => t._id === name);

        if (selectedSchedule) tempSelection.push(mappedObject(selectedSchedule));
      } else tempSelection = tempSelection.filter((s) => s.id !== name);
    }

    setSelectedRows(tempSelection);
  };

  const openScheduleTaskCallback = (id) => {
    setTasksScheduleId(id);
  };

  const openChangeAssigneeCallback = async (schedule, data) => {
    const payload =
      schedule.status === 'published'
        ? {
            assignedUsers: data.users,
            assignedGroups: data.groups,
            allowToUpdateCustomizedTasks: true,
          }
        : schedule.status === 'draft'
        ? { assignedUsers: data.users, assignedGroups: data.groups }
        : null;
    const API_URL =
      schedule.status === 'published'
        ? 'UPDATE_SCHEDULE_ASSIGNEES'
        : schedule.status === 'draft'
        ? 'UPDATE_DRAFT_SCHEDULE'
        : null;
    if (API_URL) {
      const result = await CallAPI(API_URL, payload, schedule._id);
      if (result.status) {
        toast.success('Schedule assignees updated successfully');
        fetchScheduleList(currentPage);
      }
    }
  };

  const handleCloseEditModal = () => {
    setEditMode(null);
    setScheduleDetail(null);
  };

  useEffect(() => {
    fetchTaskLabelList();
    fetchCompanyFieldList();
  }, []);

  useEffect(() => {
    fetchScheduleList();
  }, [filter]);

  return (
    <div>
      <div className='d-flex flex-column'>
        <label className='theme-size-2 theme-text-black-1'>{t('text_schedule_tasks')}</label>
        <label>{t('text_schedule_tasks_description')}</label>
      </div>
      <ScheduleListHeader
        openEditScheduleModal={() => setEditMode('edit')}
        filter={filter}
        setFilter={setFilter}
        handleDebouncedFilter={handleDebouncedFilter}
        taskLabelsOptions={taskLabelsOptions}
        companyFieldOptions={companyFieldOptions}
        selectedRows={selectedRows}
        actionsCallBack={actionsCallBack}
      />
      <TableComponent
        header={schedulesHeaderList}
        data={scheduleList}
        props={{
          actionsType: 'schedule',
          translateValueFunc: (val) => t(val),
          openScheduleTaskCallback,
          openChangeAssigneeCallback,
          actionsCallBack,
        }}
        loader={loading}
        isFieldSelectionEnabled={true}
        isActionsEnabled={true}
        isSelectionEnabled={true}
        selectionKey={'_id'}
        onSelection={onRowsSelection}
        selectedRows={selectedRows}
        headerCellBorder='vertical'
        bodyCellBorder='both'
        noWrap
        noDataComponent={
          <div className='w-100 d-flex flex-column gap-2 justify-content-center align-items-center py-3'>
            <img className='' src={scheduleIcon} />
            <span className='theme-text-black-1 theme-font-inter-semi-bold theme-size-1_2'>
              {t('text_no_tasks')}
            </span>
            <span>{t('text_currently_no_tasks_in_schedule')}</span>
            <FormButton
              text={'button_add_schedule'}
              variant='green-1'
              iconClasses='svg-white'
              onClick={() => setEditMode('edit')}
              icon={plusIcon}
            />
          </div>
        }
      />

      {totalRecords > 0 && (
        <TablePagination
          totalRecords={totalRecords}
          currentPageRecords={scheduleList.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          displayEntries={displayEntries}
          setDisplayEntries={setDisplayEntries}
          listingFunction={fetchScheduleList}
        />
      )}

      {editMode && (
        <ScheduleEditModal
          handleClose={handleCloseEditModal}
          refreshList={fetchScheduleList}
          editMode={scheduleDetail ? editMode : ''}
          scheduleData={scheduleDetail}
        />
      )}
      {tasksScheduleId && (
        <ScheduleTasks
          scheduleId={tasksScheduleId}
          taskLabelsOptions={taskLabelsOptions}
          closeModal={() => setTasksScheduleId(null)}
        />
      )}
    </div>
  );
};

export default ScheduleList;
